/** @jsx jsx */

import { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { css, jsx } from '@emotion/core';
import 'services/ie11closestPolyfill';
import pubSub from 'services/pubSub';
import { useSelector } from 'react-redux';
import { State } from 'store/store';
import { InteractionContext } from 'components/Layouts/ContainerLogical';
import { speechSynthesisCancel } from 'services/speechSynthesisCancel';

let speechSynth;
if ('speechSynthesis' in window) {
  speechSynth = new SpeechSynthesisUtterance();
}

console.log({ speechSynth });

const PlayBtn = ({ play = true, color = '' }) => {
  const style = {
    stroke: color,
    strokeWidth: 1.5,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  } as { [key: string]: string | number };

  return play ? (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2C17.5228 2 22 6.47716 22 12C22 17.5228 17.5228 22 12 22C6.47716 22 2 17.5228 2 12C2 6.47716 6.47716 2 12 2Z"
        style={style}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15.0501 12.467C14.3211 13.253 12.3371 14.583 11.3221 15.01C11.1601 15.078 10.7471 15.222 10.6581 15.224C10.4691 15.23 10.2871 15.124 10.1991 14.954C10.1651 14.888 10.0651 14.457 10.0331 14.265C9.93811 13.681 9.88911 12.774 9.89011 11.862C9.88911 10.905 9.94211 9.95495 10.0481 9.37695C10.0761 9.22095 10.1581 8.86195 10.1821 8.80395C10.2271 8.69595 10.3091 8.61095 10.4081 8.55795C10.4841 8.51695 10.5711 8.49495 10.6581 8.49795C10.7471 8.49995 11.1091 8.62695 11.2331 8.67595C12.2111 9.05595 14.2801 10.434 15.0401 11.244C15.1081 11.317 15.2951 11.513 15.3261 11.553C15.3971 11.643 15.4321 11.752 15.4321 11.862C15.4321 11.964 15.4011 12.068 15.3371 12.155C15.3041 12.2 15.1131 12.4 15.0501 12.467Z"
        style={style}
        fill={color}
      />
    </svg>
  ) : (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2C17.5228 2 22 6.47716 22 12C22 17.5228 17.5228 22 12 22C6.47716 22 2 17.5228 2 12C2 6.47716 6.47716 2 12 2Z"
        style={style}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M13.4057 9H10.5943C9.61429 9 9 9.69362 9 10.6755V13.3244C9 14.3063 9.61143 15 10.5943 15H13.4057C14.3886 15 15 14.3063 15 13.3244V10.6755C15 9.69362 14.3886 9 13.4057 9Z"
        style={style}
        fill={color}
      />
    </svg>
  );
};

export const TextToSpeech = props => {
  const [color, setColor] = useState<string | undefined>(undefined);
  const [canPlay, setCanPlay] = useState<boolean>(true);

  const voice = useSelector((state: State) => {
    if (!speechSynth) {
      return null;
    }

    const voices = window.speechSynthesis.getVoices();

    return (
      voices.find(v => {
        return v.name === state.theme.textToSpeechVoice;
      }) || voices[0]
    );
  });

  const chameleonText = useRef<HTMLElement>();
  const textToSpeechBtn = useRef<HTMLDivElement>(null);

  const onClick = useCallback(
    e => {
      if (!speechSynth) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      speechSynthesisCancel();

      if (chameleonText.current && canPlay) {
        //

        // this approach was somewhat problems
        // reading '.' periods as ' dot ' !!!
        // const doc = new DOMParser().parseFromString(content, 'text/html');
        // speechSynth.text = doc.body.innerText || content;

        const text = chameleonText.current.innerText || '';
        speechSynth.text = text;

        speechSynth.voice = voice;
        window.speechSynthesis.speak(speechSynth);
      }

      setCanPlay(canPlay => {
        return !canPlay;
      });
    },
    [canPlay, voice?.name]
  );

  useEffect(() => {
    if (!textToSpeechBtn.current) {
      return;
    }

    chameleonText.current = textToSpeechBtn.current.closest(
      '[class*="chameleon-text"]'
    ) as HTMLElement;
    const chameleonTextP = chameleonText.current?.querySelector(
      'p, h1, h2, h3, h4, h5, h6, li'
    );

    if (chameleonTextP) {
      const cssObj = window.getComputedStyle(chameleonTextP, null);
      setColor(cssObj.color);
    }
  });

  useEffect(() => {
    if (!speechSynth) {
      return;
    }

    const onStart = e => {
      // console.log('speechSynth starts speeching...');
    };
    const onEnd = e => {
      setCanPlay(true);
    };

    speechSynth.addEventListener('start', onStart);
    speechSynth.addEventListener('end', onEnd);

    return () => {
      speechSynth.removeEventListener('start', onStart);
      speechSynth.removeEventListener('end', onEnd);
      speechSynthesisCancel();
    };
  }, []);

  useEffect(() => {
    const pS = pubSub.subscribe('SECTION_IN_VIEW', () => {
      setCanPlay(true);
    });

    return pS;
  }, []);

  return (
    <div
      key={`text-to-speech-${color}-${canPlay.toString()}`}
      ref={textToSpeechBtn}
      css={css({
        border: 'none',
        outline: 'none',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: 24,
        height: 24,
        margin: '5px 0 0 0',
        borderRadius: '50%',
        background: 'transparent',
        fontSize: 7,
        cursor: 'pointer',

        ':hover': {
          opacity: 0.8,
        },

        zIndex: 99999,
      })}
      onClick={onClick}
      onKeyDown={e => {
        if ([' ', 'Enter'].includes(e.key)) {
          onClick(e);

          requestAnimationFrame(() => {
            textToSpeechBtn.current?.focus();
          });
        }
      }}
      role="button"
      tabIndex={0}
      aria-label="Click here to have this passage of text read aloud. Click again to stop narration"
    >
      <PlayBtn play={canPlay} color={color} />
    </div>
  );
};
